import React, { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet";
import { APIError } from "../../components/APIError";
import { useNavigator } from "../../components/AppHistory";
import { ModalRoutes } from "../../pages/ModalRoutes";
import { PageComponent } from "../../pages/routes";

import { AccountSearchResults } from "./components/AccountSearchResults";
import { SearchForm } from "./components/SearchForm";
import { SuccessNotification } from "./components/SuccessNotification";
import { useSessionStorage } from "../../hooks";
import { ExtendedContainer } from "../../components/ExtendedContainer";
import { ProtectedRoute } from "../../components/ProtectedRoute";
import { AlertBlock } from "../../components/AlertBlock";
import { AccountSearchInfo } from "../../api/services";
import { useAccountSearch } from "./hooks/useAccountSearch";

// TODO: Move this to a dedicated component if we use this more often
const Loader = () => (
  <Box data-testid="Loader" display="flex" alignItems="center" mb={3}>
    <Box clone mr={2}>
      <CircularProgress />
    </Box>
    <Typography>Loading...</Typography>
  </Box>
);

export const SearchContainer: PageComponent = ({ modals }) => {
  const { openOverlay } = useNavigator();
  const match = useRouteMatch();

  const {
    page,
    setPage,
    pageSize,
    isLastPage,
    query,
    setQuery,
    results,
    resultsCount,
    error,
    status,
    isFetching,
  } = useAccountSearch();

  const [
    selectedAccount,
    setSelectedAccount,
  ] = useSessionStorage<AccountSearchInfo | null>("selectedAccount", {
    removeOnUnmount: true,
  });

  // Handle specific actions
  const handleSearch = useCallback(
    (values: Record<string, any>) => {
      setQuery(values?.query);
    },
    [setQuery],
  );
  const handleAccountSelection = useCallback(
    (account: AccountSearchInfo) => {
      setSelectedAccount(account);
      openOverlay(`/search/verify/${account.number}`);
    },
    [openOverlay, setSelectedAccount],
  );

  return (
    <>
      <ProtectedRoute path={match.path}>
        <Helmet>
          <title>Account Search</title>
        </Helmet>
        <ExtendedContainer mx={0} maxWidth="none" mt={4}>
          <SuccessNotification />
          <SearchForm onSubmit={handleSearch} defaultValue={query} />
          {status === "loading" ? (
            <Loader />
          ) : status === "error" ? (
            <APIError
              title="We could not load the search results."
              messages={[(error as Error)?.message]}
              alertProps={{
                "data-testid": "SearchContainer__AlertBlock--error",
              }}
            />
          ) : !results ? (
            <p>Please enter a search term to start searching</p>
          ) : isFetching ? (
            <Loader /> /* This is here to explicitly hide current results when doing a new search */
          ) : !results?.length ? (
            <AlertBlock
              variant="info"
              title="No results found."
              messages={[
                <>
                  Please enter a <strong>complete</strong> account number, phone
                  number, email address, postcode or MPAN/MPRN.
                </>,
              ]}
            />
          ) : (
            <>
              <AccountSearchResults
                accounts={results}
                onAccountSelected={handleAccountSelection}
                selectedAccountNumber={selectedAccount?.number ?? null}
                onPageChanged={setPage}
                page={page}
                pageSize={pageSize}
                isLastPage={isLastPage}
                resultsCount={resultsCount}
              />
            </>
          )}
        </ExtendedContainer>
      </ProtectedRoute>
      <ModalRoutes modals={modals} />
    </>
  );
};
